"use client"

import { format } from "date-fns"
import { Calendar as CalendarIcon, Clock } from "lucide-react"
import type * as React from "react"
import { X } from "react-feather"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface DateTimePickerFieldProps {
  dateLabel?: string
  timeLabel?: string
  value?: string
  onChange: (value: string) => void
  disabled?: (date: Date) => boolean
  required?: boolean
  className?: string
  defaultTime?: string
}

export function DateTimePickerField({
  dateLabel,
  timeLabel,
  value,
  onChange,
  disabled,
  required = false,
  className,
  defaultTime,
}: DateTimePickerFieldProps) {
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      if (value) {
        const currentTime = new Date(value)
        date.setHours(currentTime.getHours())
        date.setMinutes(currentTime.getMinutes())
      } else if (defaultTime) {
        const [hours, minutes] = defaultTime.split(":")
        date.setHours(Number.parseInt(hours, 10))
        date.setMinutes(Number.parseInt(minutes, 10))
      } else {
        const now = new Date()
        date.setHours(now.getHours())
        date.setMinutes(now.getMinutes())
      }
      onChange(date.toISOString())
    }
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [hours, minutes] = e.target.value.split(":")
    const currentDate = value ? new Date(value) : new Date()
    currentDate.setHours(Number.parseInt(hours, 10))
    currentDate.setMinutes(Number.parseInt(minutes, 10))
    onChange(currentDate.toISOString())
  }

  return (
    <div className={cn("grid grid-cols-2 gap-4", className)}>
      <div className="space-y-2">
        {dateLabel && (
          <label className="text-xs text-[#9eafc7] block">
            {dateLabel}
            {required && (
              <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                必須
              </span>
            )}
          </label>
        )}
        <Popover>
          <div className="relative flex items-center">
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal h-8 text-sm border-slate-300 hover:bg-slate-50",
                  !value && "text-slate-400",
                  value && "pr-8"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                {value ? format(new Date(value), "yyyy/MM/dd") : <span>日付を選択</span>}
              </Button>
            </PopoverTrigger>
            {value && (
              <button
                type="button"
                className="absolute right-2 rounded-sm opacity-50 hover:opacity-100 transition-opacity"
                aria-label="日付をクリア"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange("")
                }}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <PopoverContent className="w-auto p-0 bg-white" align="start">
            <Calendar
              mode="single"
              selected={value ? new Date(value) : undefined}
              onSelect={handleDateSelect}
              disabled={disabled}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        {timeLabel && (
          <label className="text-xs text-[#9eafc7] block">
            {timeLabel}
            {required && (
              <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                必須
              </span>
            )}
          </label>
        )}
        <div className="relative">
          <Input
            icon={<Clock className="h-4 w-4 text-slate-400" />}
            type="time"
            value={value ? format(new Date(value), "HH:mm") : ""}
            onChange={handleTimeChange}
            className={cn(
              "w-full appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
              value && "pr-8"
            )}
          />
          {value && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm opacity-50 hover:opacity-100 transition-opacity z-10"
              aria-label="時刻をクリア"
              onClick={(e) => {
                e.stopPropagation()
                const date = new Date(value)
                date.setHours(0, 0, 0, 0)
                onChange(date.toISOString())
              }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
